/**
 * Courses API (issue #11 and #305) - course sections.
 *
 * Typed wrappers over the shared `api` client, mirroring students.ts/
 * announcements.ts's shape. `crn` is NEVER a field on `CreateCourse`/
 * `PatchCourse` — it is system-assigned at publish (AC1) and only ever
 * appears on the read-side `Course` shape.
 */
import type { SemesterStatus } from "@/lib/api/academicStructure";
import { ApiError, api } from "@/lib/api/client";
import type { ReleaseLine } from "@/lib/api/assignments";
import type { MeetingProvider } from "@/lib/api/tenant";

export type Modality = "online" | "in_person" | "hybrid";
export type CourseStatus = "draft" | "published" | "archived";

export interface TeachingAssignment {
  id: string;
  course_id?: string;
  faculty_profile_id: string;
  /** The teacher's login, when one is linked; the inbox names recipients by it. */
  faculty_user_id?: string | null;
  assignment_kind: "primary" | "co_instructor";
  assigned_at: string;
  assigned_by?: string | null;
  ended_at?: string | null;
  ended_by?: string | null;
  end_reason?: string | null;
  display_name: string;
  honorific?: string | null;
  account_link_state: "not_linked" | "linked";
  portal_access: "none" | "active" | "inactive";
}

export interface Course {
  id: string;
  program_id: string | null;
  program_name: string | null;
  semester_id: string | null;
  semester_name: string | null;
  /**
   * The term's own state, carried on the course read (issue #95). A section in
   * a `closed` term is historical: it can be recorded and corrected, but it
   * cannot be published, take new enrolments, or generate a class schedule.
   * Null when the section is not assigned to a term at all.
   */
  semester_status: SemesterStatus | null;
  semester_start_date: string | null;
  semester_end_date: string | null;
  crn: string | null;
  code: string;
  title: string;
  title_ar: string | null;
  credits: number | null;
  modality: Modality;
  primary_instructor_faculty_profile_id: string | null;
  has_active_primary_instructor?: boolean;
  can_teach?: boolean;
  is_primary_instructor?: boolean;
  teaching_team?: TeachingAssignment[];
  meeting_days: string | null;
  meeting_time: string | null;
  meeting_weekdays?: number[] | null;
  meeting_starts_at?: string | null;
  meeting_ends_at?: string | null;
  timezone: string | null;
  meeting_provider: MeetingProvider;
  meeting_url: string | null;
  location: string | null;
  capacity: number | null;
  status: CourseStatus;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  enrolled_count: number;
}

/** No `crn` field — structural enforcement of AC1 (crn is publish-assigned). */
export interface CreateCourse {
  program_id?: string | null;
  semester_id?: string | null;
  code: string;
  title: string;
  title_ar?: string | null;
  credits?: number | null;
  modality?: Modality;
  primary_instructor_faculty_profile_id?: string | null;
  meeting_weekdays?: number[] | null;
  meeting_starts_at?: string | null;
  meeting_ends_at?: string | null;
  timezone?: string | null;
  meeting_provider?: MeetingProvider;
  meeting_url?: string | null;
  location?: string | null;
  capacity?: number | null;
  status?: CourseStatus;
  image_url?: string | null;
}

/**
 * `expected_updated_at` is the section version the edit was made against. A
 * stale one changes nothing and answers 409 `{message, current}`; without it
 * the save is unguarded.
 */
export type PatchCourse = Partial<CreateCourse> & { expected_updated_at?: string };

export interface CourseDiscardReadiness {
  course_id: string;
  code: string;
  title: string;
  eligible: boolean;
  blockers: string[];
}

export interface DiscardTestCourseRequest {
  confirm_code: string;
  reason: string;
}

export interface CourseDiscardReceipt {
  receipt_id: string;
  course_id: string;
  code: string;
  title: string;
  discarded_at: string;
}

/**
 * A new draft section of a course in another term. Without
 * `allow_additional_section`, a term that already holds a section of the same
 * code answers 409 `section_exists_in_term` naming it.
 */
export interface OfferCourseAgainRequest {
  semester_id: string;
  allow_additional_section?: boolean;
}

export interface SectionInTerm {
  id: string;
  code: string;
  title: string;
  status: CourseStatus;
  crn: string | null;
  semester_name: string | null;
}

/** The sections a 409 `section_exists_in_term` names, or null for any other refusal. */
export function sectionsAlreadyInTerm(error: unknown): SectionInTerm[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const detail = error.detail;
  if (typeof detail !== "object" || detail === null) return null;
  const { code, existing } = detail as { code?: unknown; existing?: unknown };
  return code === "section_exists_in_term" && Array.isArray(existing) ? (existing as SectionInTerm[]) : null;
}

/** Shared with AttendancePage's per-session meeting override, which offers the
 *  SAME two enums — the session override's DB CHECK constraints (migration 056)
 *  mirror the course columns exactly, so a second copy of these maps could only
 *  ever drift out of agreement with the course form. */
export const MODALITY_OPTIONS: Modality[] = ["online", "in_person", "hybrid"];

export const MODALITY_LABEL: Record<Modality, string> = {
  online: "Online",
  in_person: "In person",
  hybrid: "Hybrid",
};

export const MEETING_PROVIDER_LABEL: Record<MeetingProvider, string> = {
  zoom: "Zoom",
  google_meet: "Google Meet",
  in_person: "In person",
  other: "Other",
};

export interface CourseFilters {
  program_id?: string;
  semester_id?: string;
  status?: string;
  q?: string;
  learning?: boolean;
  include_archived?: boolean;
}

function toQuery(filters: CourseFilters): string {
  const params = new URLSearchParams();
  if (filters.program_id) params.set("program_id", filters.program_id);
  if (filters.semester_id) params.set("semester_id", filters.semester_id);
  if (filters.status) params.set("status", filters.status);
  if (filters.q) params.set("q", filters.q);
  if (filters.learning) params.set("learning", "true");
  if (filters.include_archived) params.set("include_archived", "true");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const coursesApi = {
  list: (filters: CourseFilters = {}) => api.get<Course[]>(`/courses${toQuery(filters)}`),
  get: (id: string) => api.get<Course>(`/courses/${id}`),
  create: (body: CreateCourse) => api.post<Course>("/courses", body),
  patch: (id: string, body: PatchCourse) => api.patch<Course>(`/courses/${id}`, body),
  /** Publish only the saved section revision that the registrar reviewed. */
  publish: (id: string, expectedUpdatedAt: string) => api.patch<Course>(`/courses/${id}`, { status: "published" as CourseStatus, expected_updated_at: expectedUpdatedAt }),
  discardReadiness: (id: string) =>
    api.get<CourseDiscardReadiness>(`/courses/${id}/discard-readiness`),
  discard: (id: string, body: DiscardTestCourseRequest) =>
    api.delete<CourseDiscardReceipt>(`/courses/${id}`, body),
  offerAgain: (id: string, body: OfferCourseAgainRequest) =>
    api.post<Course>(`/courses/${id}/offer-again`, body),
  /** Who will mark and release new work here, as its learners will read it once published. Teaching team only. */
  releaseLine: (id: string) => api.get<ReleaseLine>(`/courses/${id}/release-line`),
  teachingTeam: (id: string, includeHistory = false) =>
    api.get<TeachingAssignment[]>(
      `/courses/${id}/teaching-team${includeHistory ? "?include_history=true" : ""}`,
    ),
  addCoInstructor: (id: string, facultyProfileId: string) =>
    api.post<TeachingAssignment>(`/courses/${id}/teaching-team/co-instructors`, {
      faculty_profile_id: facultyProfileId,
    }),
  endTeachingAssignment: (courseId: string, assignmentId: string, reason: string) =>
    api.post<TeachingAssignment>(
      `/courses/${courseId}/teaching-team/${assignmentId}/end`,
      { reason },
    ),
};
